import React, { useMemo } from 'react'
import { motion } from 'framer-motion'

/** Cinematic ease — decisive, but with enough time to read the cascade. */
const ease = [0.22, 1, 0.36, 1]

/** Matches :root project tokens in Project.css */
const ROUTE_ACCENTS = {
  '/projects/crimsonflags': '#cc4142',
  '/projects/menumaker': '#7581ee',
  '/projects/igws': '#f23a3f',
  '/projects/bintibiz': '#dd5a92',
  '/projects/electricfox': '#eeaf75',
  '/projects/qfwellness': '#34D399',
  '/projects/diningwithtara': '#3d6b4f',
}

const DEFAULT_PRISM = ['#4fd8ff', '#2f6bff', '#7c5cff', '#dd4aff']
const VOID = '#030308'
const LAYER_COUNT = 5

const COVER_DURATION = 0.92
const REVEAL_DURATION = 1.05
const STAGGER = 0.07

/** Match the longest staggered layer (+ buffer so phase changes never cut a tween short). */
export const WAVE_COVER_MS = Math.ceil((COVER_DURATION + (LAYER_COUNT - 1) * STAGGER) * 1000) + 120
export const WAVE_REVEAL_MS = Math.ceil((REVEAL_DURATION + (LAYER_COUNT - 1) * STAGGER) * 1000) + 120
/** Hold while the new route mounts under the seal — long enough for layout/paint to settle. */
export const WAVE_HOLD_MS = 200

const clamp = (n, min, max) => Math.min(max, Math.max(min, n))

const hexToHsl = (hex) => {
  let h = hex.replace('#', '')
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  const r = parseInt(h.slice(0, 2), 16) / 255
  const g = parseInt(h.slice(2, 4), 16) / 255
  const b = parseInt(h.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l: l * 100 }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let hue = 0
  switch (max) {
    case r: hue = (g - b) / d + (g < b ? 6 : 0); break
    case g: hue = (b - r) / d + 2; break
    default: hue = (r - g) / d + 4; break
  }
  return { h: hue * 60, s: s * 100, l: l * 100 }
}

const hslToHex = (h, s, l) => {
  const hh = ((h % 360) + 360) % 360
  const ss = clamp(s, 0, 100) / 100
  const ll = clamp(l, 0, 100) / 100
  const c = (1 - Math.abs(2 * ll - 1)) * ss
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1))
  const m = ll - c / 2
  let r = 0
  let g = 0
  let b = 0
  if (hh < 60) [r, g, b] = [c, x, 0]
  else if (hh < 120) [r, g, b] = [x, c, 0]
  else if (hh < 180) [r, g, b] = [0, c, x]
  else if (hh < 240) [r, g, b] = [0, x, c]
  else if (hh < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  const to = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

/** Soft tint / shade / neighbor-hue steps around a brand accent. */
export const buildWavePalette = (accentHex) => {
  if (!accentHex) return [...DEFAULT_PRISM, VOID]
  const { h, s, l } = hexToHsl(accentHex)
  const sat = clamp(s, 42, 92)
  return [
    hslToHex(h, clamp(sat - 6, 35, 90), clamp(l + 22, 55, 82)),
    hslToHex(h, sat, clamp(l + 6, 42, 68)),
    hslToHex(h, clamp(sat + 4, 40, 95), clamp(l, 36, 58)),
    hslToHex((h + 14) % 360, clamp(sat - 2, 38, 90), clamp(l - 16, 22, 44)),
    VOID,
  ]
}

/** Accent for a pathname — project brand, else null (site prism). */
export const getRouteAccent = (pathname = '') => {
  const path = pathname.replace(/\/$/, '') || '/'
  return ROUTE_ACCENTS[path] || null
}

const seeded = (seed) => {
  let s = seed % 2147483647
  if (s <= 0) s += 2147483646
  return () => {
    s = (s * 16807) % 2147483647
    return (s - 1) / 2147483646
  }
}

const pointsToWavePath = (points) => {
  let d = `M0 0 H${points[0].x.toFixed(2)}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[Math.min(points.length - 1, i + 2)]
    const cp1x = p1.x + (p2.x - p0.x) / 6
    const cp1y = p1.y + (p2.y - p0.y) / 6
    const cp2x = p2.x - (p3.x - p1.x) / 6
    const cp2y = p2.y - (p3.y - p1.y) / 6
    d += ` C${cp1x.toFixed(2)} ${cp1y.toFixed(2)} ${cp2x.toFixed(2)} ${cp2y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`
  }
  d += ' H0 Z'
  return d
}

/** One static irregular edge per band — soft sweep, not a jagged zipper. */
const buildLayerPath = (layerIndex) => {
  const seg = 12
  const randY = seeded(1103 + layerIndex * 97)
  // Gentler Y spacing — still uneven, but less spiky
  const weights = Array.from({ length: seg }, () => 0.7 + randY() * 0.7)
  const weightSum = weights.reduce((a, b) => a + b, 0)
  const ys = [0]
  let acc = 0
  for (let i = 0; i < seg - 1; i++) {
    acc += (weights[i] / weightSum) * 100
    ys.push(acc)
  }
  ys.push(100)

  const randX = seeded(4200 + layerIndex * 61)
  const base = 108 + layerIndex * 0.25
  const amp = 3.2 + layerIndex * 0.55
  const points = ys.map((y) => {
    const t = y / 100
    const sweep = Math.sin(t * Math.PI) * (1.6 + layerIndex * 0.2)
    const n =
      Math.sin(t * (4.2 + layerIndex * 0.6) + layerIndex) * 0.45 +
      Math.sin(t * (9.1 + layerIndex * 0.4) + layerIndex * 1.3) * 0.22 +
      (randX() - 0.5) * 0.28
    return { x: clamp(base + sweep + n * amp, 103, 116), y }
  })
  return pointsToWavePath(points)
}

const LAYER_PATHS = Array.from({ length: LAYER_COUNT }, (_, i) => buildLayerPath(i))

const sealedPhases = new Set(['covering', 'covered', 'holding'])

/** vw beats %-of-self for compositor transforms on these oversized layers. */
const X_IN = '0vw'
const X_LEFT = '-120vw'
const X_RIGHT = '120vw'

const layerTransition = (phase, index) => {
  if (phase === 'covering' || phase === 'holding' || phase === 'covered') {
    // Same tween config through the seal so a phase handoff can't snap mid-cover
    return {
      type: 'tween',
      duration: COVER_DURATION,
      ease,
      delay: index * STAGGER,
    }
  }
  if (phase === 'revealing') {
    return {
      type: 'tween',
      duration: REVEAL_DURATION,
      ease,
      delay: (LAYER_COUNT - 1 - index) * STAGGER,
    }
  }
  // idle — already parked off-screen
  return { type: 'tween', duration: 0 }
}

/**
 * Controlled wave wipe.
 * @param {'idle' | 'covering' | 'covered' | 'holding' | 'revealing'} phase
 * @param {string | null} accent - destination brand hex (null = prism default)
 * @param {number} cycle - increments each cover so layers remount cleanly from the left
 */
export const WaveWipe = ({ phase = 'idle', accent = null, cycle = 0 }) => {
  const active = phase !== 'idle'
  const palette = useMemo(() => buildWavePalette(accent), [accent])
  const targetX = sealedPhases.has(phase) ? X_IN : X_RIGHT

  return (
    <div
      className={`wave-wipe${active ? ' is-active' : ''}${phase === 'revealing' ? ' is-revealing' : ''}`}
      aria-hidden="true"
    >
      {palette.map((color, index) => (
        <motion.div
          key={`${cycle}-${index}`}
          className={`wave-wipe-layer${index === palette.length - 1 ? ' wave-wipe-layer--void' : ''}`}
          initial={{ x: X_LEFT }}
          animate={{ x: targetX }}
          transition={layerTransition(phase, index)}
          transformTemplate={({ x }) => `translate3d(${x}, 0, 0)`}
        >
          <svg
            className="wave-wipe-svg"
            viewBox="0 0 120 100"
            preserveAspectRatio="none"
            shapeRendering="optimizeSpeed"
            aria-hidden="true"
          >
            <path fill={color} d={LAYER_PATHS[index] || LAYER_PATHS[0]} />
          </svg>
        </motion.div>
      ))}
    </div>
  )
}

/** Keeps page modules on the shared transition HOC without stacking extra wipes. */
const transition = (OgComponent) => {
  return (props) => <OgComponent {...props} />
}

export default transition
